if (!window.__menuInitialized) {
  window.__menuInitialized = true;

  const MOBILE_MQ = window.matchMedia("(max-width: 1199px)");

  class Menu {
    constructor(root) {
      this.root = root;
      this.parentItems = root.querySelectorAll(
        ".menu-main-dropdown__item[data-handle]",
      );
      this.childBoards = root.querySelectorAll(
        ".menu-child-link-board[data-parent-handle]",
      );
      this.grandchildBoards = root.querySelectorAll(
        ".menu-grandchild-link-board[data-child-key]",
      );
      this.parentHandlesWithNonEmptyBoards = new Set(
        [...this.childBoards]
          .filter((b) => !!b?.querySelector(".menu-child-link-board__link"))
          .map((b) => b?.dataset?.parentHandle)
          .filter(Boolean),
      );
      this.rightPanel = root.querySelector(".menu-main-dropdown__right");
      this.childrenCol = root.querySelector(".menu-main-dropdown__children");
      this.grandchildrenCol = root.querySelector(
        ".menu-main-dropdown__grandchildren",
      );
      this._ensureGlobalBackdrop();
      this.headerDrawer = null;
      this.isOpen = false;
      this._suppressHoverUntil = 0;
      this.root.dataset.mobileLevel = "0";
      this.childDisclosureButtons = root.querySelectorAll("button[data-child-key]");
      this.activeParentHandle = null;
      this.activeChildKey = null;
      this.parentButtons = [...root.querySelectorAll('button.menu-main-dropdown__item[data-handle]')];
      this.parentLinks = [...root.querySelectorAll('a.menu-main-dropdown__item[data-handle]')];
      this.parentHandlesWithChildren = new Set(
        this.parentButtons.map((item) => item.dataset.handle).filter(Boolean)
      );
      this.childButtons = [...root.querySelectorAll('button[data-child-key]')];
      this.childKeysWithGrandchildren = new Set(
        this.childButtons.map((item) => item.dataset.childKey).filter(Boolean)
      );
      this.autoDisclosure = root.dataset.autoDisclosure === "true";
      this.triggerAnchor = this.root.closest(".menu-trigger-anchor");
      this.clickToOpen = this.triggerAnchor?.dataset?.clickToOpen !== "false";
      this.leftScroll = this.root.querySelector(".menu-main-dropdown__left-scroll");
      this.closeButtons = [...this.root.querySelectorAll(".menu-main-dropdown__close")];
      this.closeButtons.forEach((btn) => {
        btn.addEventListener("click", () => this.close());
      });
      this._rootOriginalParent = this.root.parentNode;
      this._rootOriginalNextSibling = this.root.nextSibling;

      if (this.leftScroll) {
        this.leftScroll.addEventListener("scroll", () => this._updateLeftScrollGradient(), { passive: true });
      }

      this.root.addEventListener("mouseenter", () => {
        if (MOBILE_MQ.matches) return;
        if (this.clickToOpen) return;
        this._clearMenuCloseTimer();
        this._clearTriggerHoverTimer();
      });

      this.root.addEventListener("mouseleave", (event) => {
        if (MOBILE_MQ.matches) return;
        if (this.clickToOpen) return;

        const nextTarget = event.relatedTarget;
        const movingIntoTrigger =
          nextTarget instanceof Node && this._isInAnyTrigger(nextTarget);

        if (!movingIntoTrigger) {
          this._scheduleMenuClose();
        }
      });


      this._onBreakpointChange = this._onBreakpointChange.bind(this);
      if ("onchange" in MOBILE_MQ) {
        MOBILE_MQ.addEventListener("change", this._onBreakpointChange);
      } else {
        MOBILE_MQ.addListener(this._onBreakpointChange);
      }

      this._bindDesktopPositioning();
      this._bindParentItems();
      this._bindChildItems();
      this._bindScrollFades();
      this._bindMobileBackHeadings();
      this._bindDesktopTriggers();
      this._bindOutsideClose();
      this._bindKeyboardNav();
      // Drawer patch deferred to activate() — must not intercept until isTesting=true
    }

    _getFirstParentItem() {
      return this.root.querySelector(".menu-main-dropdown__item[data-handle]");
    }

    _focusFirstParentItem() {
      const firstItem = this._getFirstParentItem();
      if (!firstItem) return;
      firstItem.focus();
    }

    _openInitialDesktopPanel() {
      if (!this.autoDisclosure) {
        this._focusFirstParentItem();
        return;
      }

      const firstButtonWithBoard = [...this.parentItems].find((item) => {
        return (
          item.tagName === "BUTTON" &&
          this.parentHandlesWithNonEmptyBoards.has(item.dataset.handle)
        );
      });

      if (!firstButtonWithBoard) {
        this._focusFirstParentItem();
        return;
      }

      const handle = firstButtonWithBoard.dataset.handle;
      this._showChildren(handle);

      firstButtonWithBoard.focus();
    }

    _ensureGlobalBackdrop() {
      let el = document.querySelector("[data-menu-backdrop]");
      if (!el) {
        el = document.createElement("div");
        el.className = "menu-main-dropdown-backdrop";
        el.setAttribute("data-menu-backdrop", "");
        el.hidden = true;
      }
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
      this.backdrop = el;
    }

    // ============================== A/B activation ==============================

    activate(isTesting) {
      this.testingActive = isTesting;
      if (isTesting) this._patchMobileDrawer();
    }

    _isTestingActive() {
      return this.header?.dataset?.ab === "b";
    }

    _scheduleHover(fn) {
      this._clearHoverTimer();
      this._hoverTimer = setTimeout(fn, 400);
    }

    _clearHoverTimer() {
      if (this._hoverTimer) {
        clearTimeout(this._hoverTimer);
        this._hoverTimer = null;
      }
    }

    _bindParentItems() {
      this.parentItems.forEach((item) => {
        const handle = item.dataset.handle;
        if (!handle) return;

        const hasChildren = this.parentHandlesWithChildren.has(handle);

        item.addEventListener("mouseenter", () => {
          if (MOBILE_MQ.matches) return;
          if (performance.now() < this._suppressHoverUntil) return;
          if (!hasChildren) return;
          this._scheduleHover(() => this._showChildren(handle));
        });

        item.addEventListener("mouseleave", () => {
          if (MOBILE_MQ.matches) return;
          this._clearHoverTimer();
        });

        item.addEventListener("focus", () => {
          if (MOBILE_MQ.matches) return;
          if (performance.now() < this._suppressHoverUntil) return;
          if (!hasChildren) return;
          this._showChildren(handle);
        });

        if (item.tagName === "BUTTON") {
          item.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (MOBILE_MQ.matches) {
              this._drillIntoParent(handle);
            } else {
              this._showChildren(handle);
            }
          });
        }
      });
    }

    _bindChildItems() {
      this.childDisclosureButtons.forEach((item) => {
        const key = item.dataset.childKey;

        item.addEventListener("mouseenter", () => {
          if (MOBILE_MQ.matches) return;
          this._scheduleHover(() => this._showGrandchildren(key));
        });
        item.addEventListener("mouseleave", () => {
          if (MOBILE_MQ.matches) return;
          this._clearHoverTimer();
        });
        item.addEventListener("focus", () => {
          if (MOBILE_MQ.matches) return;
          this._showGrandchildren(key);
        });

        item.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (MOBILE_MQ.matches) this._drillIntoChild(key);
          else this._showGrandchildren(key);
        });
      });
    }

    _bindDesktopTriggers() {
      this.headerSection =
        this.root.closest(".shopify-section-header") || document;
      this.header =
        this.headerSection.querySelector(".header") ||
        document.querySelector(".header");

      this.desktopTriggers = this.headerSection.querySelectorAll("[data-menu-trigger]");
      if (!this.desktopTriggers.length) return;

      this.desktopTriggers.forEach((trigger) => {
        trigger.addEventListener("click", (e) => {
          e.preventDefault();
          if (!this._isTestingActive()) return;
          if (MOBILE_MQ.matches) return;

          this._lastTrigger = trigger;

          if (!this.clickToOpen) {
            return;
          }

          this.toggle();
        });

        trigger.addEventListener("mouseenter", () => {
          if (!this._isTestingActive()) return;
          if (MOBILE_MQ.matches) return;
          if (this.clickToOpen) return;

          this._lastTrigger = trigger;
          this._clearMenuCloseTimer();
          this._scheduleTriggerOpen(trigger);
        });

        trigger.addEventListener("mouseleave", (event) => {
          if (!this._isTestingActive()) return;
          if (MOBILE_MQ.matches) return;
          if (this.clickToOpen) return;

          this._clearTriggerHoverTimer();

          const nextTarget = event.relatedTarget;
          const movingIntoMenu =
            nextTarget instanceof Node && this.root.contains(nextTarget);

          if (!movingIntoMenu && this.isOpen) {
            this._scheduleMenuClose();
          }
        });

        trigger.addEventListener("focus", () => {
          if (!this._isTestingActive()) return;
          if (MOBILE_MQ.matches) return;

          this._lastTrigger = trigger;

          if (this.clickToOpen) return;
          this.open();
        });

        trigger.addEventListener("keydown", (event) => {
          if (!this._isTestingActive()) return;
          if (MOBILE_MQ.matches) return;

          if (event.key === "ArrowDown") {
            event.preventDefault();
            this._lastTrigger = trigger;

            const wasOpen = this.isOpen;
            if (!wasOpen) {
              this.open();
              return;
            }

            this._openInitialDesktopPanel();
          }

          if (event.key === "Escape" && this.isOpen) {
            event.preventDefault();
            this.close();
          }
        });
      });
    }

    _patchMobileDrawer() {
      if (this._drawerPatched) return;
      this._drawerPatched = true;
      const drawerEl =
        this.headerSection?.querySelector("header-drawer") ||
        document.querySelector("header-drawer");

      if (!drawerEl) return;
      this.headerDrawer = drawerEl;

      drawerEl.addEventListener(
        "click",
        (event) => {
          if (!event.target.closest("summary")) return;
          if (!MOBILE_MQ.matches) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.isOpen ? this.close() : this.open();
        },
        { capture: true },
      );

      const originalClose = drawerEl.closeMenuDrawer?.bind(drawerEl);
      const originalFocusOut = drawerEl.onFocusOut?.bind(drawerEl);
      const menu = this;

      if (originalClose) {
        drawerEl.closeMenuDrawer = function (event, elementToFocus) {
          if (menu.isOpen) return;
          originalClose(event, elementToFocus);
        };
      }
      if (originalFocusOut) {
        drawerEl.onFocusOut = function (event) {
          if (menu.isOpen) return;
          originalFocusOut(event);
        };
      }
    }

    _bindOutsideClose() {
      document.addEventListener("click", (event) => {
        if (!this.isOpen) return;
        if (this.root.contains(event.target)) return;
        if (this._isInAnyTrigger(event.target)) return;
        if (this.headerDrawer?.contains(event.target)) return;
        this.close(false);
      });

      if (this.backdrop) {
        this.backdrop.addEventListener("click", () => {
          if (!this.isOpen) return;
          this.close(false);
        });
      }

      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape" && this.isOpen) this.close();
      });
    }

    _bindMobileBackHeadings() {
      this.root.querySelectorAll("[data-menu-back='parent']").forEach((button) => {
        button.addEventListener("click", () => {
          if (!MOBILE_MQ.matches) return;
          this._drillBack();
        });
      });

      this.root.querySelectorAll("[data-menu-back='child']").forEach((button) => {
        button.addEventListener("click", () => {
          if (!MOBILE_MQ.matches) return;
          this._drillBack();
        });
      });
    }

    _drillIntoParent(handle) {
      if (!this.parentHandlesWithChildren.has(handle)) return;
      this._showChildren(handle);
      this._setMobileLevel(1);
    }

    _drillIntoChild(key) {
      this._showGrandchildren(key);
      this._setMobileLevel(2);
    }

    _drillBack() {
      const level = parseInt(this.root.dataset.mobileLevel || "0", 10);
      if (level <= 0) return;

      if (level === 2) {
        this._setMobileLevel(1);

        window.clearTimeout(this._mobileCleanupTimer);
        this._mobileCleanupTimer = window.setTimeout(() => {
          if (!MOBILE_MQ.matches) return;
          this._showGrandchildren(null);
        }, 220);

        return;
      }

      if (level === 1) {
        this._setMobileLevel(0);

        window.clearTimeout(this._mobileCleanupTimer);
        this._mobileCleanupTimer = window.setTimeout(() => {
          if (!MOBILE_MQ.matches) return;
          this._showChildren(null);
        }, 220);
      }
    }

    _setMobileLevel(n) {
      if (!MOBILE_MQ.matches) {
        this.root.dataset.mobileLevel = String(n);
        return;
      }

      const nextLevel = String(n);
      const currentLevel = this.root.dataset.mobileLevel || "0";

      if (currentLevel === nextLevel) return;

      this.root.classList.add("is-mobile-transitioning");
      this.root.dataset.mobileLevel = nextLevel;

      window.clearTimeout(this._mobileTransitionTimer);
      this._mobileTransitionTimer = window.setTimeout(() => {
        this.root.classList.remove("is-mobile-transitioning");
      }, 240);
    }

    _showChildren(handle) {
      const hasBoard = !!handle && this.parentHandlesWithChildren.has(handle);

      this._resetChildAndGrandchildState();
      this.activeParentHandle = handle || null;
      this.activeChildKey = null;

      this.parentItems.forEach((item) => {
        const isActive = !!handle && item.dataset.handle === handle;
        item.classList.toggle("is-active", isActive);

        if (item.tagName === "BUTTON") {
          item.setAttribute("aria-expanded", isActive ? "true" : "false");
        }
      });

      if (this.rightPanel) this.rightPanel.hidden = !hasBoard;
      if (this.childrenCol) this.childrenCol.hidden = !hasBoard;
      if (this.grandchildrenCol) this.grandchildrenCol.hidden = true;

      this.childBoards.forEach((board) => {
        board.hidden = !hasBoard || board.dataset.parentHandle !== handle;
      });

      this.grandchildBoards.forEach((board) => {
        board.hidden = true;
      });

      if (!hasBoard) {
        requestAnimationFrame(() => {
          this._refreshScrollFades();
        });
        return;
      }

      if (!MOBILE_MQ.matches) {
        if (this.autoDisclosure) {
          this._openInitialGrandchildForParent(handle);
        } else {
          this._showGrandchildren(null);
        }
      }
      requestAnimationFrame(() => {
        this._refreshScrollFades();
      })
    }

    _showGrandchildren(childKey) {
      this.activeChildKey = childKey || null;

      this.root.querySelectorAll("button[data-child-key]").forEach((item) => {
        const isActive = !!childKey && item.dataset.childKey === childKey;
        item.classList.toggle("is-active", isActive);
        item.setAttribute("aria-expanded", isActive ? "true" : "false");
      });

      if (this.grandchildrenCol) this.grandchildrenCol.hidden = !childKey;

      this.grandchildBoards.forEach((board) => {
        board.hidden = !childKey || board.dataset.childKey !== childKey;
      });
      requestAnimationFrame(() => {
        this._refreshScrollFades();
      });
    }

    open() {
      if (this.isOpen) return;

      this.isOpen = true;
      this._suppressHoverUntil = performance.now() + 350;
      this._clearMenuCloseTimer();
      this._clearTriggerHoverTimer();

      const onMobile = MOBILE_MQ.matches;

      const headerEl =
        this.headerSection?.matches(".shopify-section-header")
          ? this.headerSection
          : document.querySelector(".shopify-section-header");
      if (headerEl) {
        document.documentElement.style.setProperty(
          "--header-bottom-position",
          `${Math.round(headerEl.getBoundingClientRect().bottom)}px`,
        );
      }

      // if (onMobile && this.headerDrawer) {
      //   const details = this.headerDrawer.mainDetailsToggle;
      //   const summary = details?.querySelector("summary");

      //   if (details && !details.open) details.open = true;
      //   summary?.setAttribute("aria-expanded", "true");
      //   document.body.classList.add(
      //     "overflow-hidden-" + this.headerDrawer.dataset.breakpoint,
      //   );
      // }
      if (onMobile && this.headerDrawer) {
        const summary = this.headerDrawer.mainDetailsToggle?.querySelector("summary");
        summary?.setAttribute("aria-expanded", "true");
      }

      this._clearHoverTimer();
      this._collapseMenuState();

      if (onMobile && this.root.parentNode !== document.body) {
        document.body.appendChild(this.root);
      }

      this.root.hidden = false;
      this.root.removeAttribute("hidden");

      if (this.backdrop) {
        this.backdrop.hidden = false;
        this.backdrop.removeAttribute("hidden");
        requestAnimationFrame(() => {
          this.backdrop.classList.add("is-open");
        });
      }

      requestAnimationFrame(() => {
        if (!this.isOpen) return;
        this.root.hidden = false;
        this.root.removeAttribute("hidden");
        this.root.classList.add("menu-main-dropdown--open");
      });

      document.body.classList.add("menu-main-dropdown-scroll-lock");
      this._syncTriggerExpanded();

      if (onMobile) {
        requestAnimationFrame(() => {
          this._focusFirstParentItem();
          this._refreshScrollFades();
          this._updateLeftScrollGradient();
        });
      } else {
        requestAnimationFrame(() => {
          this._positionDesktopDropdown();
          this._openInitialDesktopPanel();
          this._updateLeftScrollGradient();
        });
      }
    }

    _returnFocusToTrigger() {
      if (MOBILE_MQ.matches && this.headerDrawer?.mainDetailsToggle) {
        const summary =
          this.headerDrawer.mainDetailsToggle.querySelector("summary");
        if (summary) {
          summary.focus();
          return;
        }
      }

      if (this._lastTrigger) {
        this._lastTrigger.focus();
        return;
      }

      if (this.desktopTriggers?.length) {
        this.desktopTriggers[0].focus();
      }
    }

    close(returnFocus = true) {
      if (!this.isOpen) return;

      this.isOpen = false;
      this._resetParentState();
      this._resetChildAndGrandchildState();
      this._setMobileLevel(0);
      this._clearMenuCloseTimer();
      this._clearTriggerHoverTimer();
      this._suppressHoverUntil = 0;

      const onMobile = MOBILE_MQ.matches;

      this._clearHoverTimer();
      this._syncTriggerExpanded();
      this._hideBackdrop();
      this._unlockScroll();

      // if (onMobile && this.headerDrawer) {
      //   const details = this.headerDrawer.mainDetailsToggle;
      //   const summary = details?.querySelector("summary");

      //   details?.classList.remove("menu-opening");
      //   summary?.setAttribute("aria-expanded", "false");

      //   let start;
      //   const tick = (time) => {
      //     if (start === undefined) start = time;
      //     if (time - start < 400) {
      //       window.requestAnimationFrame(tick);
      //       return;
      //     }

      //     details?.removeAttribute("open");
      //     this._collapseMenuState();
      //     this._closeRoot();
      //     this._returnFocusToTrigger();
      //   };

      //   this.root.classList.remove("menu-main-dropdown--open");
      //   window.requestAnimationFrame(tick);
      //   return;
      // }

      if (onMobile && this.headerDrawer) {
        const summary = this.headerDrawer.mainDetailsToggle?.querySelector("summary");
        summary?.setAttribute("aria-expanded", "false");

        this.root.classList.remove("menu-main-dropdown--open");

        let start;
        const tick = (time) => {
          if (start === undefined) start = time;
          if (time - start < 240) {
            window.requestAnimationFrame(tick);
            return;
          }

          this._collapseMenuState();
          this._closeRoot();
          if (returnFocus) this._returnFocusToTrigger();
        };

        window.requestAnimationFrame(tick);
        return;
      }

      this._collapseMenuState();
      this._closeRoot();
      if (returnFocus) this._returnFocusToTrigger();
    }

    toggle() {
      this.isOpen ? this.close() : this.open();
    }

    _isDesktop() {
      return !MOBILE_MQ.matches;
    }

    _getVisibleItems(selector, container = this.root) {
      return [...container.querySelectorAll(selector)].filter((el) => {
        if (el.hidden) return false;
        if (el.closest("[hidden]")) return false;
        if (el.offsetParent === null) return false;
        return true;
      });
    }

    _getVisibleParentItems() {
      return this._getVisibleItems(".menu-main-dropdown__item[data-handle]");
    }

    _getVisibleChildItems() {
      const visibleBoard = [...this.childBoards].find((board) => !board.hidden);
      if (!visibleBoard) return [];
      return this._getVisibleItems(
        "a.menu-child-link-board__link, button.menu-child-link-board__link--has-children",
        visibleBoard
      );
    }

    _getVisibleGrandchildItems() {
      const visibleBoard = [...this.grandchildBoards].find(
        (board) => !board.hidden,
      );
      if (!visibleBoard) return [];
      return this._getVisibleItems(
        ".menu-grandchild-link-board__link",
        visibleBoard,
      );
    }

    _focusFirstChildItem() {
      const first = this._getVisibleChildItems()[0];
      if (first) first.focus();
    }

    _focusFirstGrandchildItem() {
      const first = this._getVisibleGrandchildItems()[0];
      if (first) first.focus();
    }

    _focusItemInList(items, currentItem, direction) {
      if (!items.length) return;
      const currentIndex = items.indexOf(currentItem);
      if (currentIndex === -1) {
        items[0].focus();
        return;
      }

      let nextIndex = currentIndex + direction;
      if (nextIndex < 0) nextIndex = items.length - 1;
      if (nextIndex >= items.length) nextIndex = 0;
      items[nextIndex].focus();
    }

    _focusFirstInList(items) {
      if (items.length) items[0].focus();
    }

    _focusLastInList(items) {
      if (items.length) items[items.length - 1].focus();
    }

    _getActiveParentButton() {
      return this.root.querySelector(
        "button.menu-main-dropdown__item.is-active[data-handle]"
      );
    }

    _getActiveChildButton() {
      return this.root.querySelector("button[data-child-key].is-active");
    }

    _getParentButtonByHandle(handle) {
      if (!handle) return null;
      return this.root.querySelector(
        `button.menu-main-dropdown__item[data-handle="${CSS.escape(handle)}"]`,
      );
    }

    _getChildButtonByKey(childKey) {
      if (!childKey) return null;
      return this.root.querySelector(
        `button[data-child-key="${CSS.escape(childKey)}"]`,
      );
    }

    _bindKeyboardNav() {
      this.root.addEventListener("keydown", (event) => {
        if (!this.isOpen) return;

        const target = event.target;
        if (!(target instanceof HTMLElement)) return;

        const key = event.key;

        if (key === "Escape") {
          event.preventDefault();
          this.close();
          return;
        }

        const isParentItem = target.matches(".menu-main-dropdown__item[data-handle]");
        const isChildItem = target.matches(".menu-child-link-board__link");
        const isGrandchildItem = target.matches(".menu-grandchild-link-board__link");

        if (isParentItem) {
          this._onParentKeydown(event, target);
          return;
        }

        if (isChildItem) {
          this._onChildKeydown(event, target);
          return;
        }

        if (isGrandchildItem) {
          this._onGrandchildKeydown(event, target);
        }
      });
    }

    _onParentKeydown(event, item) {
      const key = event.key;
      const items = this._getVisibleParentItems();
      const handle = item.dataset.handle;
      const hasChildren =
        item.tagName === "BUTTON" &&
        this.parentHandlesWithNonEmptyBoards.has(handle);

      if (key === "ArrowDown") {
        event.preventDefault();
        this._focusItemInList(items, item, 1);
        return;
      }

      if (key === "ArrowUp") {
        event.preventDefault();
        this._focusItemInList(items, item, -1);
        return;
      }

      if (key === "Home") {
        event.preventDefault();
        this._focusFirstInList(items);
        return;
      }

      if (key === "End") {
        event.preventDefault();
        this._focusLastInList(items);
        return;
      }

      if (key === "ArrowRight") {
        if (!hasChildren) return;
        event.preventDefault();
        this._showChildren(handle);

        if (this._isDesktop()) {
          this._focusFirstChildItem();
        } else {
          this._drillIntoParent(handle);
          this._focusFirstChildItem();
        }
        return;
      }

      if (key === "ArrowLeft") {
        return;
      }

      if ((key === "Enter" || key === " ") && item.tagName === "BUTTON") {
        event.preventDefault();
        if (this._isDesktop()) {
          this._showChildren(handle);
          this._focusFirstChildItem();
        } else {
          this._drillIntoParent(handle);
          this._focusFirstChildItem();
        }
      }
    }

    _onChildKeydown(event, item) {
      const key = event.key;
      const items = this._getVisibleChildItems();
      const childKey = item.dataset.childKey;
      const hasGrandchildren = item.tagName === "BUTTON" && !!childKey;

      if (key === "ArrowDown") {
        event.preventDefault();
        this._focusItemInList(items, item, 1);
        return;
      }

      if (key === "ArrowUp") {
        event.preventDefault();
        this._focusItemInList(items, item, -1);
        return;
      }

      if (key === "Home") {
        event.preventDefault();
        this._focusFirstInList(items);
        return;
      }

      if (key === "End") {
        event.preventDefault();
        this._focusLastInList(items);
        return;
      }

      if (key === "ArrowRight") {
        if (!hasGrandchildren) return;
        event.preventDefault();

        if (this._isDesktop()) {
          this._showGrandchildren(childKey);
          this._focusFirstGrandchildItem();
        } else {
          this._drillIntoChild(childKey);
          this._focusFirstGrandchildItem();
        }
        return;
      }

      if (key === "ArrowLeft") {
        event.preventDefault();

        if (this._isDesktop()) {
          const activeParent = this._getActiveParentButton();
          if (activeParent) activeParent.focus();
        } else {
          this._drillBack();
          const activeParent = this._getParentButtonByHandle(this.activeParentHandle);
          if (activeParent) activeParent.focus();
          else this._focusFirstParentItem();
        }
        return;
      }

      if ((key === "Enter" || key === " ") && item.tagName === "BUTTON") {
        event.preventDefault();

        if (this._isDesktop()) {
          this._showGrandchildren(childKey);
          this._focusFirstGrandchildItem();
        } else {
          this._drillIntoChild(childKey);
          this._focusFirstGrandchildItem();
        }
      }
    }

    _onGrandchildKeydown(event, item) {
      const key = event.key;
      const items = this._getVisibleGrandchildItems();

      if (key === "ArrowDown") {
        event.preventDefault();
        this._focusItemInList(items, item, 1);
        return;
      }

      if (key === "ArrowUp") {
        event.preventDefault();
        this._focusItemInList(items, item, -1);
        return;
      }

      if (key === "Home") {
        event.preventDefault();
        this._focusFirstInList(items);
        return;
      }

      if (key === "End") {
        event.preventDefault();
        this._focusLastInList(items);
        return;
      }

      if (key === "ArrowLeft") {
        event.preventDefault();

        if (this._isDesktop()) {
          const activeChild = this._getActiveChildButton();
          if (activeChild) activeChild.focus();
        } else {
          this._drillBack();
          const activeChild = this._getChildButtonByKey(this.activeChildKey);
          if (activeChild) activeChild.focus();
          else this._focusFirstChildItem();
        }
      }
    }

    _syncTriggerExpanded() {
      this.desktopTriggers?.forEach((trigger) => {
        trigger.setAttribute("aria-expanded", this.isOpen ? "true" : "false");
      });

      const summary = this.headerDrawer?.mainDetailsToggle?.querySelector("summary");
      if (summary) {
        summary.setAttribute("aria-expanded", this.isOpen ? "true" : "false");
      }
    }

    _hideBackdrop() {
      if (!this.backdrop) return;

      this.backdrop.classList.remove("is-open");

      setTimeout(() => {
        if (!this.isOpen) this.backdrop.hidden = true;
      }, 180);
    }

    // _unlockScroll() {
    //   document.body.classList.remove("menu-main-dropdown-scroll-lock");

    //   if (this.headerDrawer?.dataset?.breakpoint) {
    //     document.body.classList.remove(
    //       "overflow-hidden-" + this.headerDrawer.dataset.breakpoint,
    //     );
    //   }
    // }

    _unlockScroll() {
      document.body.classList.remove("menu-main-dropdown-scroll-lock");
    }

    _collapseMenuState() {
      this._resetParentState();
      this._resetChildAndGrandchildState();
      this._setMobileLevel(0);
    }

    _closeRoot() {
      this.root.classList.remove("menu-main-dropdown--open");
      this.root.hidden = true;
      this.root.setAttribute("hidden", "");
      if (this._rootOriginalParent && this.root.parentNode === document.body) {
        this._rootOriginalParent.insertBefore(
          this.root,
          this._rootOriginalNextSibling,
        );
      }
    }

    _isInAnyTrigger(target) {
      return [...(this.desktopTriggers || [])].some((trigger) =>
        trigger.contains(target)
      );
    }

    _getFirstChildTriggerForParent(handle) {
      if (!handle) return null;

      const board = [...this.childBoards].find(
        (item) => item.dataset.parentHandle === handle,
      );
      if (!board) return null;

      return board.querySelector("button[data-child-key]");
    }

    _resetChildAndGrandchildState() {
      this.childBoards.forEach((board) => {
        board.hidden = true;
      });

      this.grandchildBoards.forEach((board) => {
        board.hidden = true;
      });

      this.root.querySelectorAll("button[data-child-key]").forEach((item) => {
        item.classList.remove("is-active");
        item.setAttribute("aria-expanded", "false");
      });

      if (this.childrenCol) this.childrenCol.hidden = true;
      if (this.grandchildrenCol) this.grandchildrenCol.hidden = true;
    }

    _resetParentState() {
      this.parentItems.forEach((item) => {
        item.classList.remove("is-active");

        if (item.tagName === "BUTTON") {
          item.setAttribute("aria-expanded", "false");
        }
      });

      if (this.rightPanel) this.rightPanel.hidden = true;
    }

    _openInitialGrandchildForParent(handle) {
      const activeChildBoard = [...this.childBoards].find(
        (board) => board.dataset.parentHandle === handle && !board.hidden
      );
      if (!activeChildBoard) return;

      const firstChildButton = activeChildBoard.querySelector("button[data-child-key]");
      if (!firstChildButton) {
        if (this.grandchildrenCol) this.grandchildrenCol.hidden = true;
        return;
      }

      this._showGrandchildren(firstChildButton.dataset.childKey);
    }

    _scheduleTriggerOpen(trigger) {
      this._clearTriggerHoverTimer();
      this._triggerHoverTimer = window.setTimeout(() => {
        if (!this._isTestingActive()) return;
        if (MOBILE_MQ.matches) return;
        this._lastTrigger = trigger;
        this.open();
      }, 400);
    }

    _clearTriggerHoverTimer() {
      if (this._triggerHoverTimer) {
        clearTimeout(this._triggerHoverTimer);
        this._triggerHoverTimer = null;
      }
    }

    _scheduleMenuClose() {
      this._clearMenuCloseTimer();
      this._menuCloseTimer = window.setTimeout(() => {
        if (!this.isOpen) return;
        if (MOBILE_MQ.matches) return;
        if (this.clickToOpen) return;
        this.close(false);
      }, 180);
    }

    _clearMenuCloseTimer() {
      if (this._menuCloseTimer) {
        clearTimeout(this._menuCloseTimer);
        this._menuCloseTimer = null;
      }
    }

    _restoreRootToOriginalParent() {
      if (
        this._rootOriginalParent &&
        this.root.parentNode !== this._rootOriginalParent
      ) {
        this._rootOriginalParent.insertBefore(
          this.root,
          this._rootOriginalNextSibling,
        );
      }
    }

    _onBreakpointChange(event) {
      const nowMobile = event.matches;

      window.clearTimeout(this._mobileCleanupTimer);
      window.clearTimeout(this._mobileTransitionTimer);
      this.root.classList.remove("is-mobile-transitioning");

      if (!this.isOpen) {
        if (!nowMobile) {
          this._restoreRootToOriginalParent();
        }
        this._collapseMenuState();
        return;
      }

      if (nowMobile) {
        if (this.root.parentNode !== document.body) {
          document.body.appendChild(this.root);
        }

        this.root.hidden = false;
        this.root.removeAttribute("hidden");
        this.root.classList.add("menu-main-dropdown--open");
        this._collapseMenuState();

        const summary =
          this.headerDrawer?.mainDetailsToggle?.querySelector("summary");
        summary?.setAttribute("aria-expanded", "true");

        requestAnimationFrame(() => {
          this._focusFirstParentItem();
          this._refreshScrollFades();
        });
        return;
      }

      this._restoreRootToOriginalParent();
      this.root.hidden = false;
      this.root.removeAttribute("hidden");
      this.root.classList.add("menu-main-dropdown--open");
      this._collapseMenuState();

      requestAnimationFrame(() => {
        this._openInitialDesktopPanel();
        this._refreshScrollFades();
      });
    }


    // Need to either remove this or use it
    destroy() {
      if (this.scrollFadeContainers?.length) {
        this.scrollFadeContainers.forEach((container) => {
          if (container._menuScrollFadeUpdate) {
            container.removeEventListener("scroll", container._menuScrollFadeUpdate);
          }
        });
      }

      window.removeEventListener("resize", this._handleScrollFadeResize);
      window.removeEventListener("resize", this._repositionDesktopDropdown);
      window.removeEventListener("scroll", this._repositionDesktopDropdown);

      if ("onchange" in MOBILE_MQ) {
        MOBILE_MQ.removeEventListener("change", this._onBreakpointChange);
      } else {
        MOBILE_MQ.removeListener(this._onBreakpointChange);
      }
    }

    _positionDesktopDropdown() {
      if (MOBILE_MQ.matches) return;

      const trigger = this._lastTrigger || this.desktopTriggers?.[0];
      if (!trigger) return;

      const anchor = trigger.closest(".menu-trigger-anchor");
      const navRow =
        trigger.closest(".header__inline-menu, .header-bottom-menu, .header-top-menu") ||
        anchor ||
        trigger;

      const triggerRect = trigger.getBoundingClientRect();
      const navRowRect = navRow.getBoundingClientRect();

      const viewportPadding = 12;
      const menuRect = this.root.getBoundingClientRect();
      const menuWidth = menuRect.width || this.root.offsetWidth || 0;

      let left = triggerRect.left;

      if (left + menuWidth > window.innerWidth - viewportPadding) {
        left = Math.max(
          viewportPadding,
          window.innerWidth - menuWidth - viewportPadding,
        );
      }

      const top = navRowRect.bottom - 1;

      this.root.style.setProperty("--menu-dropdown-left", `${Math.round(left)}px`);
      this.root.style.setProperty("--menu-dropdown-top", `${Math.round(top)}px`);
    }

    _bindDesktopPositioning() {
      this._repositionDesktopDropdown = () => {
        if (!this.isOpen) return;
        if (MOBILE_MQ.matches) return;
        this._positionDesktopDropdown();
      };

      window.addEventListener("resize", this._repositionDesktopDropdown, { passive: true });
      window.addEventListener("scroll", this._repositionDesktopDropdown, { passive: true });
    }

    _updateLeftScrollGradient() {
      if (!this.leftScroll) return;
      const el = this.leftScroll;
      const maxScroll = el.scrollHeight - el.clientHeight;
      el.classList.toggle("is-at-top", el.scrollTop <= 1);
      el.classList.toggle("is-at-bottom", maxScroll <= 1 || el.scrollTop >= maxScroll - 1);
    }

    _bindScrollFades() {
      this.scrollFadeContainers = [
        this.childrenCol,
        this.grandchildrenCol,
      ].filter(Boolean);

      this._handleScrollFadeResize = () => {
        this._refreshScrollFades();
      };

      this.scrollFadeContainers.forEach((container) => {
        const update = () => this._updateScrollFade(container);
        container._menuScrollFadeUpdate = update;
        container.addEventListener("scroll", update, { passive: true });
      });

      window.addEventListener("resize", this._handleScrollFadeResize);

      requestAnimationFrame(() => {
        this._refreshScrollFades();
      });
    }

    _updateScrollFade(container) {
      const isVisible = !container.hidden && container.offsetParent !== null;

      if (!MOBILE_MQ.matches || !isVisible) {
        container.classList.remove(
          "is-scrollable",
          "is-scrolled-top",
          "is-scrolled-bottom",
        );
        return;
      }

      const maxScroll = container.scrollHeight - container.clientHeight;
      const isScrollable = maxScroll > 1;
      const isScrolledTop = container.scrollTop > 1;
      const canScrollDown = container.scrollTop < maxScroll - 1;

      container.classList.toggle("is-scrollable", isScrollable);
      container.classList.toggle("is-scrolled-top", isScrolledTop);
      container.classList.toggle("is-scrolled-bottom", isScrollable && canScrollDown);
    }

    _refreshScrollFades() {
      if (!this.scrollFadeContainers?.length) return;
      this.scrollFadeContainers.forEach((container) => {
        this._updateScrollFade(container);
      });
    }

  }

  const initVariantMenu = () => {
    const menuEl = document.getElementById("menu-main-dropdown");
    if (!menuEl) return;
    window.__menu = new Menu(menuEl);

    // Race-condition guard: Shoplift may resolve and stamp data-ab='b' before
    // this deferred script runs. In that case window.__menu didn't exist when the
    // inline header script called activate(), so we self-activate here.
    const headerEl =
      document.querySelector(".shopify-section-header .header[data-ab]") ||
      document.querySelector(".header[data-ab]");
    if (headerEl?.dataset?.ab === "b") {
      window.__menu.activate(true);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initVariantMenu);
  } else {
    initVariantMenu();
  }
}
